import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { Save, Building2, Upload, Palette, Lock } from 'lucide-react'
import api from '@/lib/axios'
import { brandingApi, type BrandingConfig } from '@/api/branding'
import { useBranding } from '@/hooks/useBranding'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

interface OrgSettingsData {
  name: string
  extraction_prompt: string | null
}

interface Organization {
  id: string
  name: string
  slug: string
  extraction_prompt: string | null
}

export default function OrgSettings() {
  const queryClient = useQueryClient()
  const [saved, setSaved] = useState(false)

  const { data: org, isLoading } = useQuery<Organization>({
    queryKey: ['org-settings'],
    queryFn: () => api.get('/orgs/current').then((r) => r.data),
  })

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<OrgSettingsData>({
    defaultValues: { name: '', extraction_prompt: null },
    values: org ? { name: org.name, extraction_prompt: org.extraction_prompt || '' } : undefined,
  })

  const mutation = useMutation({
    mutationFn: (data: OrgSettingsData) => api.patch('/orgs/current', data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-settings'] })
      queryClient.invalidateQueries({ queryKey: ['me'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    },
  })

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-32 bg-gray-200 rounded" />
        </div>
      </div>
    )
  }

  return (
    <div className="p-8 max-w-2xl">
      <div className="flex items-center gap-3 mb-8">
        <Building2 className="h-6 w-6 text-blue-600" />
        <h1 className="text-2xl font-bold text-gray-900">Organization Settings</h1>
      </div>

      <form onSubmit={handleSubmit((data) => mutation.mutate(data))} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">General</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">Organization Name</Label>
              <Input
                id="name"
                {...register('name', { required: 'Name is required' })}
                placeholder="Acme Corp"
                className="mt-1"
              />
              {errors.name && (
                <p className="text-sm text-red-500 mt-1">{errors.name.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                value={org?.slug || ''}
                disabled
                className="mt-1 bg-gray-50"
              />
              <p className="text-xs text-gray-500 mt-1">Slug cannot be changed after creation.</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Default Extraction Prompt</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-500 mb-3">
              This prompt is used as the default context for all extraction jobs in your
              organization. It overrides the platform default but can be overridden per project or
              field.
            </p>
            <Textarea
              {...register('extraction_prompt')}
              rows={6}
              placeholder="e.g. These are Australian commercial contracts governed by NSW law..."
              className="font-mono text-sm"
            />
          </CardContent>
        </Card>

        <div className="flex items-center gap-3">
          <Button
            type="submit"
            disabled={!isDirty || mutation.isPending}
            className="flex items-center gap-2"
          >
            <Save className="h-4 w-4" />
            {mutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
          {saved && <span className="text-sm text-green-600">Saved!</span>}
          {mutation.isError && (
            <span className="text-sm text-red-500">Failed to save. Please try again.</span>
          )}
        </div>
      </form>

      <BrandingSection />
    </div>
  )
}


// ---------------------------------------------------------------------------
// Branding Section
// ---------------------------------------------------------------------------
function BrandingSection() {
  const queryClient = useQueryClient()
  const { isWhitelabel } = useBranding()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [brandingSaved, setBrandingSaved] = useState(false)

  const { data: branding, isLoading: brandingLoading } = useQuery<BrandingConfig>({
    queryKey: ['org-branding'],
    queryFn: brandingApi.get,
    staleTime: 5 * 60 * 1000,
  })

  const [brandForm, setBrandForm] = useState({
    brand_name: '',
    brand_primary_color: '',
    brand_favicon_url: '',
  })

  // Sync form state when branding data loads
  const [initialized, setInitialized] = useState(false)
  if (branding && !initialized) {
    setBrandForm({
      brand_name: branding.brand_name || '',
      brand_primary_color: branding.brand_primary_color || '',
      brand_favicon_url: branding.brand_favicon_url || '',
    })
    setInitialized(true)
  }

  const updateMutation = useMutation({
    mutationFn: (data: { brand_name?: string | null; brand_primary_color?: string | null; brand_favicon_url?: string | null }) =>
      brandingApi.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-branding'] })
      setBrandingSaved(true)
      setTimeout(() => setBrandingSaved(false), 2000)
    },
  })

  const uploadMutation = useMutation({
    mutationFn: (file: File) => brandingApi.uploadLogo(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-branding'] })
    },
  })

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      uploadMutation.mutate(file)
    }
  }

  const handleBrandingSave = () => {
    updateMutation.mutate({
      brand_name: brandForm.brand_name || null,
      brand_primary_color: brandForm.brand_primary_color || null,
      brand_favicon_url: brandForm.brand_favicon_url || null,
    })
  }

  if (brandingLoading) return null

  // If whitelabel is not enabled, show upgrade prompt
  if (!isWhitelabel) {
    return (
      <Card className="mt-6 opacity-75">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Palette className="h-4 w-4" />
            White-Label Branding
            <Lock className="h-4 w-4 text-muted-foreground" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Customize the platform with your own logo, brand name, and colors. Upgrade your plan to unlock white-label branding.
          </p>
          <Button variant="outline" className="mt-3" asChild>
            <a href="/billing">Upgrade to unlock</a>
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Palette className="h-4 w-4" />
          White-Label Branding
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Logo Upload */}
        <div>
          <Label>Logo</Label>
          <div className="flex items-center gap-4 mt-2">
            {branding?.brand_logo_url ? (
              <img
                src={branding.brand_logo_url}
                alt="Brand logo"
                className="h-10 w-auto max-w-[200px] object-contain rounded border p-1"
              />
            ) : (
              <div className="h-10 w-20 rounded border border-dashed flex items-center justify-center text-xs text-muted-foreground">
                No logo
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              onChange={handleLogoUpload}
              className="hidden"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
              className="flex items-center gap-2"
            >
              <Upload className="h-4 w-4" />
              {uploadMutation.isPending ? 'Uploading...' : 'Upload Logo'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            PNG, JPG, SVG, or WebP. Max 2MB.
          </p>
          {uploadMutation.isError && (
            <p className="text-xs text-red-500 mt-1">Upload failed. Please try again.</p>
          )}
        </div>

        {/* Brand Name */}
        <div>
          <Label htmlFor="brand_name">Brand Name</Label>
          <Input
            id="brand_name"
            value={brandForm.brand_name}
            onChange={(e) => setBrandForm((prev) => ({ ...prev, brand_name: e.target.value }))}
            placeholder="Your Company Name"
            className="mt-1 max-w-sm"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Replaces "DocumentIQ" throughout the platform.
          </p>
        </div>

        {/* Primary Color */}
        <div>
          <Label htmlFor="brand_primary_color">Primary Color</Label>
          <div className="flex items-center gap-3 mt-1">
            <Input
              id="brand_primary_color"
              value={brandForm.brand_primary_color}
              onChange={(e) =>
                setBrandForm((prev) => ({ ...prev, brand_primary_color: e.target.value }))
              }
              placeholder="#3b82f6"
              className="max-w-[160px] font-mono"
            />
            {brandForm.brand_primary_color && (
              <div
                className="h-8 w-8 rounded border"
                style={{ backgroundColor: brandForm.brand_primary_color }}
              />
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Hex color code for the primary accent color.
          </p>
        </div>

        {/* Favicon URL */}
        <div>
          <Label htmlFor="brand_favicon_url">Favicon URL</Label>
          <Input
            id="brand_favicon_url"
            value={brandForm.brand_favicon_url}
            onChange={(e) =>
              setBrandForm((prev) => ({ ...prev, brand_favicon_url: e.target.value }))
            }
            placeholder="https://example.com/favicon.ico"
            className="mt-1 max-w-md"
          />
          <p className="text-xs text-muted-foreground mt-1">
            URL to a custom favicon. Leave blank to use the default.
          </p>
        </div>

        {/* Sidebar Preview */}
        <div>
          <Label>Preview</Label>
          <div className="mt-2 border rounded-lg p-4 bg-muted/30 max-w-xs">
            <div className="flex items-center gap-2">
              {branding?.brand_logo_url ? (
                <img
                  src={branding.brand_logo_url}
                  alt="Preview"
                  className="h-6 w-auto max-w-[100px] object-contain"
                />
              ) : (
                <img src="/algoscale-logo.png" alt="Default" className="h-6" />
              )}
              <span className="text-base font-bold">
                {brandForm.brand_name || 'DocumentIQ'}
              </span>
            </div>
            {brandForm.brand_primary_color && (
              <div className="mt-3 flex items-center gap-2">
                <div
                  className="h-6 w-full rounded text-white text-xs flex items-center justify-center font-medium"
                  style={{ backgroundColor: brandForm.brand_primary_color }}
                >
                  Primary Color
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Save */}
        <div className="flex items-center gap-3">
          <Button
            onClick={handleBrandingSave}
            disabled={updateMutation.isPending}
            className="flex items-center gap-2"
          >
            <Save className="h-4 w-4" />
            {updateMutation.isPending ? 'Saving...' : 'Save Branding'}
          </Button>
          {brandingSaved && <span className="text-sm text-green-600">Saved!</span>}
          {updateMutation.isError && (
            <span className="text-sm text-red-500">Failed to save branding.</span>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
